import styled from '@emotion/styled';
import {
  DateSelectArg,
  DatesSetArg,
  EventApi,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  EventInput,
} from '@fullcalendar/core';
import {DragMetaInput} from '@fullcalendar/core/internal';
import interactionPlugin, {
  Draggable,
  EventResizeDoneArg,
} from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import moment from 'moment/moment';
import React, {useCallback, useContext, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';
import {generateRandomUnsafeId, useMountEffect} from 'webapp-lib/utils';

import {TimeEntryParams} from '@lexian/api/timetracker-api';
import {showSimpleConfirmationDialog} from '_/sharedComponents/ConfirmationDialog';

import {
  CALENDAR_DRAGGABLE_CARD,
  CALENDAR_DRAGGABLE_CONTENT,
  CALENDAR_FAVORITE_CONTAINER,
  CALENDAR_RECENT_CONTAINER,
} from '../timetracker.constants';
import {
  DraggableTimeEntrySuggestion,
  EventApiWithTimEntryProps,
  TimeEntry,
  TimeEntryCalendarEventExtendedProps,
  TimerFacade,
} from '../timetracker.types';
import {toSeconds} from '../utilities/date.util';
import {eventToParams, timeEntryToParams} from '../utilities/mapping.util';

import {EditTimeEntryDialog} from './EditTimeEntryDialog';
import {AdditionalInfo, TimeEntryCalendarEvent} from './TimeEntryCalendarEvent';
import {CalendarAPIContext} from './TimetrackerButton';

const START_WORK_HOUR = '8:00';
const END_WORK_HOUR = '17:00';

const SLOT_DURATION_MINUTES = 15;
const SNAP_TO_RESOLUTION_MINUTES = 5;
const LABEL_INTERVAL_HOURS = 1;
export const DEFAULT_EVENT_DURATION_SECONDS = toSeconds(60);

const DRAG_TO_CREATE_EVENT_PREVIEW: 'highlight' | 'normal' = 'normal';

// Our two-line event content (header row with play button ≈ 32px + title line
// ≈ 19px) needs more height than FullCalendar's default short-event threshold
// of 30px assumes: a 30/45-minute event (~32/48px) would still render two
// lines and clip the title. Below this height the time and title render side
// by side instead (the built-in fc-timegrid-event-short behavior).
const EVENT_SHORT_HEIGHT_PX = 55;

type CalendarColumnProps = {
  handleEventsChanged: (updatedEvents: EventApiWithTimEntryProps[]) => void;
  initialEvents: EventApiWithTimEntryProps[];
  timer: TimerFacade;
  onDayChanged: (date: Date) => void;
  /** Signals a running drag/resize so the caller can pause polling reloads. */
  onInteractionChanged: (active: boolean) => void;
  createTimeEntry: (params: TimeEntryParams) => Promise<void>;
  updateTimeEntry: (
    timeEntryId: string,
    params: TimeEntryParams
  ) => Promise<void>;
  deleteTimeEntry: (timeEntryId: string) => Promise<void>;
};

function getEventDataFromCard(cardEl: HTMLElement): DragMetaInput {
  const contentEl: HTMLElement | null = cardEl.querySelector(
    `#${CALENDAR_DRAGGABLE_CONTENT}`
  );
  if (!contentEl?.dataset.template) {
    return {id: generateRandomUnsafeId()};
  }

  const {title, defaultDurationSeconds, casus, description} = JSON.parse(
    contentEl.dataset.template
  ) as DraggableTimeEntrySuggestion;
  const extendedProps: TimeEntryCalendarEventExtendedProps = {
    casus,
    description,
  };

  const newEvent: EventInput = {
    id: generateRandomUnsafeId(),
    title,
    duration: {
      seconds: defaultDurationSeconds ?? DEFAULT_EVENT_DURATION_SECONDS,
    },
    extendedProps,
  };
  return {...newEvent, create: true};
}

function convertToEventInput(event: EventApi): EventInput {
  return {
    id: event.id,
    title: event.title,
    start: event.start!,
    end: event.end ?? new Date(),
    groupId: event.groupId,
    allDay: event.allDay,
    url: event.url,
    classNames: event.classNames,
    display: event.display,
    editable: event.extendedProps.mutable,
    overlap: event.overlap,
    constraint: event.constraint,
    backgroundColor: event.backgroundColor,
    borderColor: event.borderColor,
    textColor: event.textColor,
    color: event.backgroundColor,
    extendedProps: event.extendedProps,
  };
}

export function Calendar({
  handleEventsChanged,
  initialEvents,
  timer,
  onDayChanged,
  onInteractionChanged,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
}: CalendarColumnProps): React.ReactElement {
  const {t} = useTranslation('timetracker');

  const calendarRef = useRef<FullCalendar>(null!);
  const draggableCardRef = useRef<{
    favorites: Draggable;
    recent: Draggable;
  } | null>(null);

  const [selectedEvent, setSelectedEvent] =
    useState<EventApiWithTimEntryProps | null>(null);
  // distinguishes a freshly drag-created entry (createTimeEntry) from editing an existing one (updateTimeEntry)
  const [isNewTimeEntry, setIsNewTimeEntry] = useState(false);

  const calendarAPI = useCallback(() => calendarRef.current.getApi(), []);
  const calendarContext = useContext(CalendarAPIContext);

  useMountEffect(() => calendarContext.setCalendarAPI(calendarAPI()));

  // init draggable favorites
  useMountEffect(() => {
    if (draggableCardRef.current) {
      return;
    }

    const draggableFavoriteContainer = document.getElementById(
      CALENDAR_FAVORITE_CONTAINER
    );
    const draggableRecentContainer = document.getElementById(
      CALENDAR_RECENT_CONTAINER
    );
    if (!draggableRecentContainer || !draggableFavoriteContainer) {
      return;
    }

    const cardSelector = `div[data-id=${CALENDAR_DRAGGABLE_CARD}]`;
    const options = {
      itemSelector: cardSelector,
      eventData: getEventDataFromCard,
    };
    draggableCardRef.current = {
      favorites: new Draggable(draggableFavoriteContainer, options),
      recent: new Draggable(draggableRecentContainer, options),
    };

    return () => {
      if (draggableCardRef.current) {
        draggableCardRef.current.favorites.destroy();
        draggableCardRef.current.recent.destroy();
        draggableCardRef.current = null;
      }
    };
  });

  const handleEventClassNames = (content: EventContentArg) => {
    if (content.isMirror) {
      return [];
    }

    const classes: string[] = [];
    const event = content.event as EventApiWithTimEntryProps;

    // Background
    if (!event.extendedProps.mutable) {
      classes.push('work-log-immutable');
    }

    // Right border color
    classes.push('work-log-right-border');
    if (event.extendedProps.ongoing) {
      classes.push('work-log-is-tracking');
    } else if (!event.extendedProps.casus) {
      classes.push('work-log-unassigned');
    } else if (event.extendedProps.billability !== 'BILLABLE') {
      classes.push('work-log-nonbillable');
    }

    return classes;
  };

  const handleRenderEventContent = (eventContent: EventContentArg) => (
    <TimeEntryCalendarEvent eventContent={eventContent} timer={timer} />
  );

  const handleDatesSet = (arg: DatesSetArg) => {
    onDayChanged(arg.start);
  };

  const handleDateRangeSelected = (selectInfo: DateSelectArg) => {
    const event = calendarAPI().addEvent({
      id: generateRandomUnsafeId(),
      start: selectInfo.start,
      end: selectInfo.end,
      editable: true,
    });

    setIsNewTimeEntry(true);
    setSelectedEvent(event);
    calendarAPI().unselect(); // clear date selection
  };

  const handleEventClicked = (eventInfo: EventClickArg) => {
    if (!eventInfo.event.extendedProps.mutable) {
      return;
    }
    setIsNewTimeEntry(false);
    setSelectedEvent(eventInfo.event);
  };

  const handleEventEdited = async (timeEntry: TimeEntry) => {
    if (isNewTimeEntry) {
      // the create + reload replaces the temporary drag-preview event
      await createTimeEntry(timeEntryToParams(timeEntry, false));
      return;
    }

    const event = calendarAPI().getEventById(timeEntry.id);
    if (!event) {
      showSnackbar({
        text: t('common:snackbar.error.unexpectedError'),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const ongoing =
      (event.extendedProps as TimeEntryCalendarEventExtendedProps).ongoing ??
      false;
    await updateTimeEntry(timeEntry.id, timeEntryToParams(timeEntry, ongoing));
  };

  // persist start/end changes from drag or resize
  const persistEventChange = (event: EventApi) => {
    updateTimeEntry(event.id, eventToParams(event)).then(() => {});
  };

  const handleEventDropped = (arg: EventDropArg) => {
    persistEventChange(arg.event);
  };

  const handleEventResized = (arg: EventResizeDoneArg) => {
    persistEventChange(arg.event);
  };

  const handleEventDeleted = (event: EventApiWithTimEntryProps) => {
    showSimpleConfirmationDialog({
      headerText: t('components.timeEntries.delete.header'),
      message: t('components.timeEntries.delete.message'),
      buttonProps: {
        danger: true,
        confirmationButtonText: t('common:actions.delete'),
      },
      onConfirmed: async () => {
        await deleteTimeEntry(event.id);
        setSelectedEvent(null);
      },
    });
  };

  function handleEditDialogClosed() {
    if (selectedEvent) {
      // delete selected event range if nothing was entered in the dialog
      const event = calendarAPI().getEventById(selectedEvent.id);
      if (event?.title === '') {
        event.remove();
      }
    }
    setSelectedEvent(null);
  }

  const dialog =
    selectedEvent !== null ? (
      <EditTimeEntryDialog
        data={selectedEvent}
        isOpen={true}
        onClose={handleEditDialogClosed}
        onDelete={handleEventDeleted}
        onEdit={handleEventEdited}
      />
    ) : null;

  return (
    <>
      {dialog}
      <CalendarTheme>
        <FullCalendar
          ref={calendarRef}
          allDaySlot={false}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: START_WORK_HOUR,
            endTime: END_WORK_HOUR,
          }}
          buttonText={{today: t('components.timeEntries.today')}}
          datesSet={handleDatesSet} // called on init and on day navigation
          dayHeaderFormat={{
            year: 'numeric',
            month: '2-digit',
            day: 'numeric',
            weekday: 'long',
          }}
          droppable={true}
          editable={true}
          eventClassNames={handleEventClassNames}
          eventClick={handleEventClicked}
          eventContent={handleRenderEventContent}
          eventDragStart={() => onInteractionChanged(true)}
          eventDragStop={() => onInteractionChanged(false)}
          eventDrop={handleEventDropped}
          eventResize={handleEventResized}
          eventResizeStart={() => onInteractionChanged(true)}
          eventResizeStop={() => onInteractionChanged(false)}
          eventShortHeight={EVENT_SHORT_HEIGHT_PX}
          eventsSet={handleEventsChanged} // called after events are initialized/added/changed/removed
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: false,
            hour12: false,
          }}
          expandRows={true}
          firstDay={1}
          headerToolbar={{
            left: 'prev',
            center: 'today',
            right: 'next',
          }}
          initialEvents={initialEvents.map(convertToEventInput)}
          initialView='timeGridDay'
          locale='de-CH'
          nowIndicator={true}
          plugins={[timeGridPlugin, interactionPlugin]}
          scrollTime={moment().subtract(2, 'hours').format('HH:mm')}
          scrollTimeReset={false}
          select={handleDateRangeSelected}
          selectable={true}
          selectMirror={DRAG_TO_CREATE_EVENT_PREVIEW === 'normal'}
          slotDuration={{minutes: SLOT_DURATION_MINUTES}}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: false,
            hour12: false,
          }}
          slotLabelInterval={{hours: LABEL_INTERVAL_HOURS}}
          snapDuration={{minutes: SNAP_TO_RESOLUTION_MINUTES}}
          weekends={true}
        />
      </CalendarTheme>
    </>
  );
}

const CalendarTheme = styled.div`
  --fc-button-bg-color: var(--color-core-primary);
  --fc-button-hover-bg-color: var(--color-core-primary);
  --fc-button-border-color: var(--color-box-border);
  --fc-button-hover-border-color: var(--color-state-hover);
  --fc-today-bg-color: transparent;

  height: 100%;

  .work-log-immutable {
    background-color: var(--color-core-grey) !important;
  }

  .work-log-right-border {
    border-right: 10px solid transparent;

    &.work-log-unassigned {
      border-right-color: var(--color-alerts-error);
    }
    &.work-log-nonbillable {
      border-right-color: var(--color-alerts-info);
    }
    &.work-log-is-tracking {
      border-right-color: var(--color-state-active);
    }
  }

  & .fc-media-screen {
    height: 100%;
  }

  & .fc-timegrid-slot {
    height: 1em;
  }

  & .fc-event {
    cursor: auto;
  }

  .fc-timegrid-event-short {
    & ${AdditionalInfo} {
      display: none !important;
    }

    .fc-event-main-frame {
      align-items: center;
    }
  }

  .fc-timegrid-now-indicator-container {
    .fc-timegrid-now-indicator-line {
      border-width: 3px 0 0;
    }

    .fc-timegrid-now-indicator-arrow {
      scale: 2;
    }
  }

  & .fc-scrollgrid {
    border: 1px solid var(--color-core-primary);
  }

  & .fc-timegrid-slot {
    border-color: var(--color-box-border);
  }

  & .fc-toolbar.fc-header-toolbar {
    margin-bottom: var(--gutter);
  }

  .fc-event-resizer:after {
    content: '';
    display: block;
    border-top: 4px double;
    border-color: var(--fc-event-text-color);
    margin: 0 auto;
    width: 12px;
  }
`;
